/**
 * Tool Call Accumulator
 *
 * Accumulates tool call data from streaming LLM responses (OpenAI-style deltas).
 */
import { tryParseJSON } from "./IncrementalJSONParser";
export function accumulateToolCall(state, delta) {
    const { index, id, function: fn } = delta;
    if (typeof index !== "number") {
        return null;
    }
    let accumulator = state.toolCallAccumulators.get(index);
    if (!accumulator) {
        accumulator = {
            index,
            id: id || "",
            name: fn?.name || "",
            arguments: "",
        };
        state.toolCallAccumulators.set(index, accumulator);
    }
    if (id) {
        accumulator.id = id;
    }
    if (fn?.name) {
        accumulator.name = fn.name;
    }
    if (fn?.arguments) {
        accumulator.arguments += fn.arguments;
    }
    return accumulator;
}
export function getAccumulatedToolCalls(state) {
    return Array.from(state.toolCallAccumulators.values()).sort((a, b) => a.index - b.index);
}
export function getToolCallByIndex(state, index) {
    return state.toolCallAccumulators.get(index);
}
export function getToolCallByName(state, name) {
    for (const acc of Array.from(state.toolCallAccumulators.values())) {
        if (acc.name === name) {
            return acc;
        }
    }
    return undefined;
}
export function hasToolCalls(state) {
    return state.toolCallAccumulators.size > 0;
}
export function getToolCallCount(state) {
    return state.toolCallAccumulators.size;
}
export function hasCompleteArguments(accumulator) {
    const trimmed = accumulator.arguments.trim();
    if (!trimmed)
        return false;
    const lastChar = trimmed[trimmed.length - 1];
    return lastChar === "}" || lastChar === "]";
}
export function allToolCallsComplete(state) {
    if (state.toolCallAccumulators.size === 0) {
        return false;
    }
    for (const acc of Array.from(state.toolCallAccumulators.values())) {
        if (!hasCompleteArguments(acc)) {
            return false;
        }
    }
    return true;
}
export function parseToolCallArguments(accumulator) {
    if (!accumulator.arguments) {
        return null;
    }
    const result = tryParseJSON(accumulator.arguments);
    return result.success ? result.data : null;
}
export function toExecutableToolCalls(state) {
    return getAccumulatedToolCalls(state).map((acc) => ({
        id: acc.id,
        type: "function",
        function: {
            name: acc.name,
            arguments: acc.arguments,
        },
    }));
}
export function clearToolCalls(state) {
    state.toolCallAccumulators.clear();
}
export function clearToolCallByIndex(state, index) {
    state.toolCallAccumulators.delete(index);
}
